import { useCallback, useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import ParrotMark from '../components/common/ParrotMark'
import Spinner from '../components/common/Spinner'
import { useSamplePlayer } from '../hooks/useSamplePlayer'
import { clock } from '../lib/clock'
import { faDigits } from '../lib/numerals'
import {
  LEVELS,
  STEPS,
  answersOf,
  ctaLabel,
  initialQuestions,
  markAsked,
  markOnboardingSeen,
  questionNumber,
  questionsReducer,
  samplesFor,
  savePendingAnswers,
} from '../lib/onboarding'
import { onboardingSamples } from '../services/onboarding'
import { useAuthStore } from '../store/authStore'

const PRIMARY = 'h-14 rounded-[14px] bg-brand-600 text-[15.5px] font-bold text-white shadow-[0_6px_16px_rgba(43,90,150,0.22)] transition-colors hover:bg-brand-700 dark:text-slate-900'
const OPTION = 'flex w-full items-center gap-3 rounded-[16px] border-[1.5px] border-line bg-surface px-4 py-3.5 text-start transition hover:border-brand-200 hover:bg-brand-50/40'

function PlayGlyph({ paused }) {
  return paused ? (
    <svg width="16" height="16" viewBox="0 0 24 24" className="fill-current" aria-hidden="true">
      <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" className="-me-0.5 fill-current" aria-hidden="true">
      <path d="M8 5.2v13.6a1 1 0 0 0 1.53.85l10.2-6.8a1 1 0 0 0 0-1.7L9.53 4.35A1 1 0 0 0 8 5.2z" />
    </svg>
  )
}

/** The three slides of the design (page 1): the copy, a skip link, a dot indicator, one primary action. */
function Carousel({ onDone }) {
  const { t } = useTranslation('onboarding')
  const [step, setStep] = useState(0)
  const last = step === STEPS.length - 1
  const current = STEPS[step]
  return (
    <>
      <div className="flex justify-start">
        <button type="button" onClick={onDone} className="h-10 rounded-[10px] px-3 text-[12.5px] font-semibold text-ink-3 hover:bg-surface-2">
          {t('skip')}
        </button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-[26px] text-center">
        <span className="grid size-[168px] place-items-center rounded-full bg-brand-50">
          <ParrotMark size={92} className="text-brand-600" />
        </span>
        <div className="flex flex-col gap-3" aria-live="polite">
          <h1 className="text-[25px] font-bold leading-[1.5] tracking-[-0.4px] text-ink">{current.title}</h1>
          <p className="text-sm leading-[1.95] text-ink-2 text-pretty">{current.body}</p>
        </div>
      </div>

      <div className="flex flex-col gap-[18px]">
        <div className="flex justify-center gap-[7px]" aria-hidden="true">
          {STEPS.map((slide, index) => (
            <span key={slide.key} data-dot className={`h-[7px] rounded-full transition-all duration-200 ${index === step ? 'w-[22px] bg-brand-600' : 'w-[7px] bg-track'}`} />
          ))}
        </div>
        <button type="button" onClick={() => (last ? onDone() : setStep(step + 1))} className={PRIMARY}>
          {ctaLabel(step)}
        </button>
      </div>
    </>
  )
}

/** One level's samples on the listening screen: up to three rows with play, the sentence and its length, then «همین سطح». */
function LevelSamples({ level, state, player, onPick }) {
  const { t } = useTranslation('onboarding')
  const samples = samplesFor(state, level)
  return (
    <section className="space-y-2 rounded-[16px] border border-line bg-surface p-3.5">
      <h2 className="text-sm font-bold text-ink">{t(`questions.level.${level}`)}</h2>
      {samples.length === 0 ? (
        <p className="text-xs text-ink-3">{state.levels || state.samplesFailed ? t('questions.listen.noSamples') : t('questions.listen.loading') + '…'}</p>
      ) : (
        <ul className="space-y-1.5">
          {samples.map((sample, index) => {
            const id = `${level}:${sample.position}`
            const active = player.playingId === id && player.isPlaying
            return (
              <li key={id} className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label={active ? t('questions.listen.pauseAria', { n: faDigits(index + 1) }) : t('questions.listen.playAria', { n: faDigits(index + 1) })}
                  onClick={() => player.toggle(id, sample.audioUrl)}
                  className={`grid size-9 shrink-0 place-items-center rounded-full ${active ? 'bg-brand-600 text-white' : 'bg-brand-50 text-brand-700'}`}
                >
                  <PlayGlyph paused={active} />
                </button>
                <span dir="ltr" className="lesson-text min-w-0 flex-1 truncate text-start text-[14px] text-ink">
                  {sample.textEn}
                </span>
                <span dir="ltr" className="tnum shrink-0 text-[11px] text-ink-3">
                  {clock(Math.round((sample.durationMs ?? 0) / 1000))}
                </span>
              </li>
            )
          })}
        </ul>
      )}
      <button type="button" onClick={() => onPick(level)} className="h-10 w-full rounded-[10px] bg-brand-50 text-[12.5px] font-bold text-brand-700 transition hover:bg-brand-100">
        {t('questions.listen.pick')}
      </button>
    </section>
  )
}

/**
 * The two questions (V3-w): the level — or «نمی‌دانم», which plays the
 * samples of `onboarding_samples` before asking again — and the minutes a
 * day, from the server's `minuteOptions`. `lib/onboarding.js` owns every
 * transition; this only draws the step and hands the taps over.
 */
function Questions({ state, dispatch, player, onDismiss, existing }) {
  const { t } = useTranslation('onboarding')
  const step = state.step
  const { stop } = player

  // Leaving the listening screen silences whatever sample was playing.
  useEffect(() => {
    if (step !== 'listen') stop()
  }, [step, stop])

  return (
    <>
      <div className="flex items-center justify-between">
        {step === 'level' ? (
          <span className="text-[12.5px] font-semibold text-ink-3">{existing ? t('questions.existingNote') : ' '}</span>
        ) : (
          <button type="button" onClick={() => dispatch({ type: 'back' })} className="h-10 rounded-[10px] px-3 text-[12.5px] font-semibold text-ink-3 hover:bg-surface-2">
            {t('questions.back')}
          </button>
        )}
        <button type="button" onClick={onDismiss} className="h-10 rounded-[10px] px-3 text-[12.5px] font-semibold text-ink-3 hover:bg-surface-2">
          {t('questions.later')}
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-5 pt-4">
        <div className="flex flex-col gap-2">
          <span className="text-[11.5px] font-semibold text-brand-600">{t('questions.progress', { n: faDigits(questionNumber(step)) })}</span>
          <h1 className="text-[23px] font-bold leading-[1.5] tracking-[-0.3px] text-ink">{t(`questions.${step}.title`)}</h1>
          <p className="text-[13px] leading-[1.9] text-ink-2 text-pretty">{t(`questions.${step}.body`)}</p>
        </div>

        {step === 'level' && (
          <div className="space-y-2">
            {LEVELS.map((level) => (
              <button key={level} type="button" data-level={level} onClick={() => dispatch({ type: 'level/picked', level })} className={OPTION}>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-[15px] font-bold text-ink">{t(`questions.level.${level}`)}</span>
                  <span className="text-[11.5px] text-ink-2">{t(`questions.level.${level}Hint`)}</span>
                </span>
              </button>
            ))}
            <button type="button" data-level="unknown" onClick={() => dispatch({ type: 'level/unknown' })} className={`${OPTION} border-dashed`}>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-[15px] font-bold text-ink">{t('questions.level.unknown')}</span>
                <span className="text-[11.5px] text-ink-2">{t('questions.level.unknownHint')}</span>
              </span>
            </button>
          </div>
        )}

        {step === 'listen' && (
          <div className="space-y-2.5">
            {state.samplesFailed && <p className="rounded-xl bg-warn-tint px-3.5 py-2.5 text-xs text-warn-ink">{t('questions.listen.unavailable')}</p>}
            {player.error && (
              <p role="status" className="rounded-xl bg-bad-tint px-3.5 py-2.5 text-xs font-semibold text-bad-ink">
                {t('questions.listen.playFailed')}
              </p>
            )}
            {!state.levels && !state.samplesFailed ? (
              <Spinner label={t('questions.listen.loading')} />
            ) : (
              LEVELS.map((level) => <LevelSamples key={level} level={level} state={state} player={player} onPick={(picked) => dispatch({ type: 'level/picked', level: picked })} />)
            )}
          </div>
        )}

        {step === 'minutes' && (
          <div className="grid grid-cols-2 gap-2">
            {state.minuteOptions.map((minutes) => (
              <button
                key={minutes}
                type="button"
                data-minutes={minutes}
                onClick={() => dispatch({ type: 'minutes/picked', minutes })}
                className="h-16 rounded-[16px] border-[1.5px] border-line bg-surface text-[16px] font-bold text-ink transition hover:border-brand-200 hover:bg-brand-50/40"
              >
                {t('questions.minutes.option', { count: faDigits(minutes) })}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

/**
 * The first-run screen (design page 1, `onboarding`; P-onboarding of the
 * go-live plan; V3-w): the three slides, then the two questions, then
 * sign-in — the answers wait on the device and go up with the first
 * session (`authStore.syncOnboarding`). With `questionsOnly` it is the
 * signed-in learner's version (`/onboarding/questions`): an existing
 * account with no `onboardedAt` sees just the questions, once, and may
 * put them off (decision Q3).
 */
export default function Onboarding({ questionsOnly = false }) {
  const navigate = useNavigate()
  const location = useLocation()
  const syncOnboarding = useAuthStore((s) => s.syncOnboarding)
  const [stage, setStage] = useState(questionsOnly ? 'questions' : 'carousel')
  const [state, dispatch] = useReducer(questionsReducer, undefined, initialQuestions)
  const player = useSamplePlayer()

  useEffect(() => {
    let cancelled = false
    onboardingSamples()
      .then((data) => {
        if (!cancelled) dispatch({ type: 'samples/loaded', samples: data })
      })
      .catch(() => {
        if (!cancelled) dispatch({ type: 'samples/failed' })
      })
    return () => {
      cancelled = true
    }
  }, [])

  const leave = useCallback(() => {
    navigate(questionsOnly ? '/dashboard' : '/auth', { replace: true, state: location.state })
  }, [navigate, questionsOnly, location.state])

  const answers = answersOf(state)
  const level = answers?.level ?? null
  const dailyMinutes = answers?.dailyMinutes ?? null
  useEffect(() => {
    if (!level || !dailyMinutes) return
    savePendingAnswers({ level, dailyMinutes })
    markAsked()
    // With a session the answers go up now; without one they wait for sign-in.
    if (questionsOnly) void syncOnboarding()
    leave()
  }, [level, dailyMinutes, questionsOnly, syncOnboarding, leave])

  function carouselDone() {
    markOnboardingSeen()
    setStage('questions')
  }

  function dismiss() {
    markAsked()
    leave()
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-[22px] pb-[26px] pt-[22px]">
      {stage === 'carousel' ? <Carousel onDone={carouselDone} /> : <Questions state={state} dispatch={dispatch} player={player} onDismiss={dismiss} existing={questionsOnly} />}
    </div>
  )
}
